import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import cogoToast from '@successtar/cogo-toast'
import Spinner from '@renderer/components/Spinner'
import { useInterval } from '@renderer/utils/useInterval'
import MidtransUtils from '@renderer/utils/midtransUtils'
import { RiArrowGoBackLine } from 'react-icons/ri'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'

const PaymentPage: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { config: globalConfig, getPageSettings } = useConfigStore()
  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)
  const welcomeSettings = getPageSettings('welcome')
  const paymentSettings = getPageSettings('payment')
  const { id, transaction, userPreferences, setSessionState, uploadSessionState } =
    useSessionStateStore()
  const isMounted = useRef(true)

  // Helper to safely update state/logs only when component is mounted
  const safeUpdate = (callback: () => void): void => {
    if (isMounted.current) {
      callback()
    }
  }

  // States
  const [remainingTime, setRemainingTime] = useState<string | null>(null)
  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [isExpired, setIsExpired] = useState(false)
  const [isCheckingPayment, setIsCheckingPayment] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'success' | 'failed'>('pending')

  // Initialize countdown hook
  const paymentCountdown = useCountdown(paymentSettings?.paymentTimeout || 120)

  const cleanupPayment = async (): Promise<void> => {
    if (transaction?.orderId && transaction?.status !== 'settlement') {
      try {
        await fetch(`${process.env.VITE_API_URL}/api/payments/${transaction.orderId}`, {
          method: 'DELETE',
          credentials: 'include'
        })
      } catch (error) {
        console.error('Error cancelling payment:', error)
      }
    }
  }

  useEffect(() => {
    // Upload session state on mount
    uploadSessionState()
    return (): void => {
      isMounted.current = false
      setIsCheckingPayment(false)
      paymentCountdown.stop()
      cleanupPayment()
    }
  }, [])

  const calculateRemainingTime = (expiryTime: string, serverTime: string): number => {
    try {
      const expiryTimestamp = new Date(expiryTime).getTime()
      const serverTimestamp = new Date(serverTime).getTime()
      const remainingMs = expiryTimestamp - serverTimestamp
      return Math.max(0, Math.floor(remainingMs / 1000))
    } catch (error) {
      console.error('Error calculating remaining time:', error)
      return 0
    }
  }

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const initiatePayment = async (): Promise<void> => {
    try {
      // Use the transaction amount which already includes any discounts
      const finalAmount = transaction?.amount || 0
      const data = await MidtransUtils.initiatePayment(
        finalAmount,
        id || '',
        paymentSettings?.paymentTimeout || 120,
        projectId || '',
        userPreferences?.filterId || '',
        userPreferences?.frameId || '',
        transaction?.discount || 0
      )

      if (data.order_id && data.qr_url) {
        // Sanitize qrUrl to remove double slashes
        const sanitizedQrUrl = data.qr_url.replace(/([^:]\/)\/+/g, '$1')

        // Calculate initial remaining time
        const initialRemainingSeconds = calculateRemainingTime(data.expiry_time, data.server_time)

        if (initialRemainingSeconds <= 0) {
          setIsExpired(true)
          return
        }

        setSessionState({
          transaction: {
            orderId: data.order_id,
            qrUrl: sanitizedQrUrl,
            expiryTime: data.expiry_time,
            serverTime: data.server_time,
            status: 'pending',
            amount: finalAmount,
            discount: transaction?.discount || 0
          },
          timeline: {
            activityLogs: [
              {
                page: 'payment',
                type: 'payment',
                action: 'start',
                details: {
                  amount: finalAmount,
                  discount: transaction?.discount || 0,
                  orderId: data.order_id,
                  expiryTime: data.expiry_time,
                  serverTime: data.server_time,
                  remainingSeconds: initialRemainingSeconds
                },
                timestamp: new Date(data.server_time)
              }
            ]
          }
        })

        setQrUrl(sanitizedQrUrl)
        setIsExpired(false)

        // Start countdown with initial remaining time
        paymentCountdown.setRemainingSeconds(initialRemainingSeconds)
        paymentCountdown.setOnTick(() => {
          setRemainingTime(formatTime(paymentCountdown.getRemainingSeconds()))
        })
        paymentCountdown.setOnComplete(() => handleCountdownEnd())
        paymentCountdown.start()
        setIsCheckingPayment(true)
      }
    } catch (e: unknown) {
      setSessionState({
        timeline: {
          activityLogs: [
            {
              page: 'payment',
              type: 'payment',
              action: 'failure',
              details: {
                error: (e as Error).message
              },
              timestamp: new Date()
            }
          ]
        }
      })
      console.error('Error initiating payment:', e)
      cogoToast.error((e as Error).message)
    }
  }

  useEffect(() => {
    initiatePayment()

    return (): void => {
      paymentCountdown.stop()
    }
  }, [])

  useInterval(
    async () => {
      await checkPaymentStatus()
    },
    isCheckingPayment ? 5000 : null
  )

  const checkPaymentStatus = async (isButtonClick: boolean = false): Promise<void> => {
    if (!isMounted.current || !transaction?.expiryTime || !transaction?.serverTime) {
      return
    }

    try {
      const paymentData = await MidtransUtils.checkPaymentStatus(
        transaction.orderId || 'order-default-id'
      )

      // If payment has expired, update the UI
      if (paymentData.isExpired) {
        safeUpdate(() => {
          setIsCheckingPayment(false)
          setIsExpired(true)
          setSessionState({
            transaction: {
              status: 'expire'
            },
            timeline: {
              activityLogs: [
                {
                  page: 'payment',
                  type: 'payment',
                  action: 'timeout',
                  details: {
                    orderId: transaction.orderId,
                    expiryTime: transaction.expiryTime,
                    serverTime: paymentData.serverTime,
                    transactionStatus: paymentData.transactionStatus
                  },
                  timestamp: new Date(paymentData.serverTime)
                }
              ]
            }
          })
          uploadSessionState()
        })
        return
      }

      if (paymentData.paid) {
        safeUpdate(() => {
          setIsCheckingPayment(false)
          setPaymentStatus('success')
          setSessionState({
            transaction: {
              status: 'settlement'
            },
            timeline: {
              activityLogs: [
                {
                  page: 'payment',
                  type: 'payment',
                  action: 'success',
                  details: {
                    orderId: transaction.orderId,
                    serverTime: paymentData.serverTime,
                    transactionStatus: paymentData.transactionStatus
                  },
                  timestamp: new Date(paymentData.serverTime)
                }
              ]
            }
          })
          uploadSessionState()

          // Navigate after showing success screen
          setTimeout(() => {
            const nextPage = welcomeSettings?.startWithPayment ? 'filter' : 'capture'
            navigate(`/${projectId}/${nextPage}`)
          }, 3000)
        })
      } else if (isButtonClick) {
        cogoToast.error('Payment verification failed. Please try again.')
      }
    } catch (e: unknown) {
      console.error('Error checking payment status:', e)
      if (isButtonClick) {
        safeUpdate(() => {
          cogoToast.error((e as Error).message)
        })
      }
    }
  }

  const handleCountdownEnd = async (): Promise<void> => {
    if (!isMounted.current) {
      return
    }
    await checkPaymentStatus()
    safeUpdate(() => {
      setIsCheckingPayment(false)
      setRemainingTime('00:00')
      setIsExpired(true)
      setSessionState({
        transaction: {
          status: 'expire'
        },
        timeline: {
          activityLogs: [
            {
              page: 'payment',
              type: 'payment',
              action: 'timeout',
              details: {
                orderId: transaction?.orderId
              },
              timestamp: new Date()
            }
          ]
        }
      })
    })
  }

  // Call all hooks at the top level
  const cachedPaymentAccBg = useCachedBackgroundImage('/assets/samu/samu-payment-acc.webp')
  const cachedPaymentBg = useCachedBackgroundImage('/assets/samu/samu-payment.webp')
  const cachedLogo = useCachedImage('/assets/samu/samu-logo.png')
  const cachedQrUrl = useCachedImage(qrUrl || '')

  // Show only success screen, handle failure with toast
  if (paymentStatus === 'success') {
    return (
      <div
        className="relative w-full h-full bg-cover bg-center"
        style={{ backgroundImage: `url(${cachedPaymentAccBg})` }}
      />
    )
  }

  return (
    <div
      className="relative w-full h-full bg-cover bg-center"
      style={{ backgroundImage: `url(${cachedPaymentBg})` }}
    >
      <img
        alt="Samu Logo"
        src={cachedLogo}
        width={240}
        height={240}
        className="absolute bottom-0 left-6"
      />
      <div className="absolute z-[5] left-[50px] top-[50px]">
        <button
          onClick={() => navigate(`/${projectId}/checkout`)}
          className="flex items-center gap-3 rounded-full bg-carnabyTan px-6 py-3 text-juicyMango hover:opacity-90 transition-opacity"
        >
          <RiArrowGoBackLine size={36} />
          <span className="font-body-samu text-3xl">Back</span>
        </button>
      </div>
      <div className="absolute right-[200px] top-[140px] flex flex-col items-center gap-8">
        <h2 className="text-6xl text-carnabyTan font-deco-samu mb-8">
          {formatAmount(transaction?.amount ?? 0, projectCurrency)}
        </h2>
        {isExpired ? (
          <div className="w-[520px] h-[520px] flex flex-col items-center justify-center gap-12">
            <p className="text-5xl text-carnabyTan font-deco-samu">Pembayaran kadaluarsa</p>
            <button
              onClick={() => navigate(`/${projectId}`)}
              className="rounded-lg bg-juicyMango font-body-samu text-nightBrown tracking-widest px-8 py-3 text-5xl hover:brightness-110 duration-200 active:brightness-110"
            >
              Kembali
            </button>
          </div>
        ) : qrUrl ? (
          <>
            <img
              src={cachedQrUrl}
              alt="QRIS"
              className="w-[520px] h-[520px] bg-white"
              crossOrigin="anonymous"
            />
            <div className="flex flex-col items-center gap-4 mt-8">
              <button
                onClick={() => checkPaymentStatus(true)}
                className="rounded-lg bg-juicyMango font-body-samu tracking-widest shadow-2xl text-nightBrown px-12 py-6 text-5xl hover:brightness-110 duration-200 active:brightness-110"
                disabled={!qrUrl}
              >
                Konfirmasi
              </button>
              <span className="text-4xl font-body-samu text-foundationWhite">
                {remainingTime || '...'}
              </span>
            </div>
          </>
        ) : (
          <div className="w-[520px] h-[520px] flex justify-center items-center bg-white/10">
            <Spinner />
          </div>
        )}
      </div>
    </div>
  )
}

export default PaymentPage
